import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { InvestorNote } from '../../types/InvestorNote';
import { noteService } from '../../services/noteService';
import NoteForm from './NoteForm';
import ConfirmModal from '../common/ConfirmModal';
import ActionButton from '../common/ActionButton';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import './InvestorNotes.css';

interface InvestorNotesProps {
  investorId: string;
}

const InvestorNotes: React.FC<InvestorNotesProps> = ({ investorId }) => {
  const { showToast } = useToast();
  const [notes, setNotes] = useState<InvestorNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState<InvestorNote | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<{ id: string; text: string } | null>(null);

  useEffect(() => {
    loadNotes();
  }, [investorId]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const response = await noteService.getAllByInvestor(investorId);
      setNotes(response.data);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNote(null);
    setShowForm(true);
  };

  const handleEdit = (note: InvestorNote) => {
    setEditingNote(note);
    setShowForm(true);
  };

  const handleDeleteClick = (noteId: string, comentario: string) => {
    const preview = comentario.length > 50 ? comentario.substring(0, 50) + '...' : comentario;
    setNoteToDelete({ id: noteId, text: preview });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    try {
      await noteService.delete(noteToDelete.id);
      showToast('Nota eliminada correctamente');
      loadNotes();
      setShowDeleteModal(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error('Error deleting note:', error);
      showToast('Error al eliminar la nota', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingNote(null);
    loadNotes();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingNote(null);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoriaColor = (categoria: string) => {
    const colors: { [key: string]: string } = {
      'Seguimiento': 'blue',
      'Llamada': 'green',
      'Email': 'purple',
      'Reunión': 'orange',
      'WhatsApp': 'teal',
      'Documentación': 'indigo',
      'Incidencia': 'red',
      'Oportunidad': 'yellow',
      'Automático': 'gray',
    };
    return colors[categoria] || 'gray';
  };

  return (
    <div className="investor-notes">
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar eliminación"
        message={`¿Estás seguro de que deseas eliminar esta nota? "${noteToDelete?.text}"`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      <div className="notes-header">
        <h3>Histórico de Notas</h3>
        <ActionButton
          icon={FaPlus}
          text="Nueva Nota"
          onClick={handleCreate}
          variant="primary"
        />
      </div>

      {showForm && (
        <div className="note-form-container">
          <NoteForm
            investorId={investorId}
            note={editingNote}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      )}

      {loading ? (
        <div className="loading">Cargando notas...</div>
      ) : notes.length === 0 ? (
        <div className="empty-notes">
          <p>No hay notas registradas para este inversor.</p>
          <p>Haz clic en "Nueva Nota" para añadir la primera nota.</p>
        </div>
      ) : (
        <div className="notes-table-container">
          <table className="notes-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Responsable comercial</th>
                <th>Categoría</th>
                <th>Comentario / Nota</th>
                <th>Próximo Paso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id}>
                  <td className="note-fecha">
                    {formatDateTime(note.fecha)}
                  </td>
                  <td className="note-responsable">
                    {note.esAutomatica ? 'Sistema' : (note.commercialManagerName || 'Desconocido')}
                  </td>
                  <td>
                    <span className={`note-categoria categoria-${getCategoriaColor(note.categoria)}`}>
                      {note.categoria}
                    </span>
                  </td>
                  <td className="note-comentario-cell">{note.comentario}</td>
                  <td className="note-proximo-paso-cell">{note.proximoPaso || '-'}</td>
                  <td className="note-actions-cell">
                    <div className="note-actions">
                      <ActionButton
                        icon={FaEdit}
                        text="Editar Nota"
                        onClick={() => handleEdit(note)}
                        variant="edit"
                      />
                      <ActionButton
                        icon={FaTrash}
                        text="Eliminar Nota"
                        onClick={() => handleDeleteClick(note.id!, note.comentario)}
                        variant="delete"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvestorNotes;
